import React, { useEffect, useState } from 'react';
import { getDevices, removeDevice } from '../services/YourDevicesService';
import { LoggedInDevice, Device } from '../types/Device';
import { logout } from '../../auth/services/AuthService';
import { useTranslations } from '../../localization/useTranslations';
import Button from '../../components/buttons/SimpleButton/Button';

interface DevicesProps {
  setSnackbarMessage: (message: string) => void;
  setIsSnackbarOpen: (value: boolean) => void;
}

const Devices: React.FC<DevicesProps> = ({ setSnackbarMessage, setIsSnackbarOpen }) => {
  const [devices, setDevices] = useState<LoggedInDevice[] | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [isSectionLoading, setIsSectionLoading] = useState<boolean>(true);

  const translations = useTranslations();

  const formatDate = (dateString: string, isCurrentDevice: boolean) => {
    if (isCurrentDevice) {
      return translations.myAccountSettings.currentSession || 'Current session';
    }

    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    if (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    ) {
      return translations.myAccountSettings.today || 'Today';
    }

    if (
      date.getDate() === yesterday.getDate() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getFullYear() === yesterday.getFullYear()
    ) {
      return translations.myAccountSettings.yesterday || 'Yesterday';
    }

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  };

  const updateDevices = async (token: string) => {
    if (!devices || !token) {
      return;
    }

    setIsDeleting(token);

    if (token) {
      try {
        const response = await removeDevice({ token: token });

        if (response.status === 'ok') {
          const devicesCopy: LoggedInDevice[] = [...devices];
          const filteredDevices = devicesCopy.filter((device) => device.token !== token);

          setDevices(filteredDevices);

          setSnackbarMessage(
            translations.snackBarMessages.successfullyLoggedOutOfDevice || 'Successfully logged out of device.'
          );

          setIsSnackbarOpen(true);
        }
      } catch (error: any) {
        console.error('Error while removing device:', error);

        setSnackbarMessage(translations.snackBarMessages.errorLoggingOutOfDevice || 'Error logging out of device.');

        setIsSnackbarOpen(true);
      } finally {
        setIsDeleting(null);
      }
    }
  };

  useEffect(() => {
    const fetchLoggedInDevices = async () => {
      try {
        const response = await getDevices();

        const sortedDevices = response.devices.sort((a: Device, b: Device) => {
          return new Date(b.last_used_at).getTime() - new Date(a.last_used_at).getTime();
        });

        setDevices(sortedDevices);
      } catch (error: any) {
        console.error('Fetching error:', error);
      } finally {
        setIsSectionLoading(false);
      }
    };

    fetchLoggedInDevices();
  }, []);

  useEffect(() => {
    if (isDeleting === devices?.find((device) => device.is_current_device)?.token) {
      logout();
    }
  }, [devices, isDeleting]);

  if (isSectionLoading) {
    return <div className="my-settings__section my-settings__section--addresses" style={{ height: '200px' }} />;
  }

  return (
    <div className="my-settings__section my-settings__section--devices">
      <div className="my-settings__section-header">
        <h4 className="my-settings__section-title">{translations.myAccountSettings.yourDevices || 'Your devices'}</h4>
      </div>

      {devices && devices.length > 0 ? (
        <table id="my-settings-device-table" className="my-settings__devices" cellSpacing="0">
          <thead className="my-settings__table-head">
            <tr className="my-settings__table-header-row">
              <th className="my-settings__table-header basic basic-14">
                {translations.myAccountSettings.device || 'Device'}
              </th>

              <th className="my-settings__table-header basic basic-14">
                {translations.myAccountSettings.mostRecentActivity || 'Most recent activity'}
              </th>
            </tr>
          </thead>
          <tbody className="my-settings__table-body">
            {devices.map((device) => (
              <tr key={device.id} className="my-settings__table-row" data-device-id={device.id}>
                <td className="my-settings__table-data basic basic-16">
                  <span className="my-settings__table-data-inner device">
                    <span className="my-settings__data-label">{translations.myAccountSettings.device || 'Device'}</span>
                    <span className="my-settings__data-text">
                      {device.browser} on {device.platform}
                    </span>
                  </span>

                  {device.is_current_device && (
                    <span className="my-settings__table-data-inner my-settings__table-data-inner--current-device is-current-device basic basic-14">
                      {translations.myAccountSettings.thisDevice || 'This device'}
                    </span>
                  )}
                </td>

                <td className="my-settings__table-data basic basic-16 activity">
                  <span className="my-settings__data-label">
                    {translations.myAccountSettings.mostRecentActivity || 'Most recent activity'}
                  </span>
                  <span className="my-settings__data-text">
                    {formatDate(device.last_used_at, device.is_current_device)}
                  </span>
                </td>

                <td className="my-settings__table-data my-settings__table-data--button">
                  <div className="my-settings__table-data-button-wrapper">
                    <Button
                      className="my-settings__logout-button"
                      size="medium"
                      type="secondary"
                      onClick={() => updateDevices(device.token)}
                      label={
                        isDeleting === device.token
                          ? translations.myAccount.loading || 'Loading...'
                          : translations.accountNavigation.logout || 'Logout'
                      }
                      isDisabled={isDeleting === device.token}
                    ></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="my-settings__section-empty-state my-settings__section-empty-state--devices">
          <p className="my-settings__section-empty-state-text empty-message">
            {translations.myAccountSettings.noLoggedDevices || 'No logged-in devices.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default Devices;
